import { useEffect, useRef, useState } from 'react';
import type { MagicState } from '@captionflow/shared';
import { TEMPLATES_BY_ID } from '@captionflow/template-schema';
import { useEditor } from '../../state/editorStore';
import { api, ApiClientError } from '../../api/client';
import { saveEdits } from '../../lib/pipeline';

export type KmagicPhase = 'idle' | 'running' | 'ready' | 'previewing';

/** Playful, non-technical status lines shown WHILE kMagic runs. They rotate on a
 *  timer so the wait feels alive - the real internals stay on /admin. */
const MAGIC_MESSAGES = [
  'Watching your clip 👀',
  'Finding the hook 🎣',
  'Reading the room 🎨',
  'Picking the perfect style ✨',
  'Weighting every word',
  'Designing your captions',
  'Hunting the perfect B‑roll 🎬',
  'Setting the vibe 🎵',
  'Art‑directing the edit 🎬',
  'Adding a little polish 💫',
  'Making it pop',
  'Almost there…',
];

/** True when the current project state needs the person cut-out (a Behind
 *  template OR any hook set to sit behind the subject). */
function projectNeedsDepth(): boolean {
  const s = useEditor.getState();
  const tpl = TEMPLATES_BY_ID[s.templateId];
  return tpl?.renderMode === 'behind' || s.hooks.some((h) => h.behind);
}

/**
 * All of the kMagic run state + actions (start, poll, preview, apply, rollback,
 * reset), shared by the desktop panel and the mobile chat UI so the two never
 * drift. UI-agnostic - the components render however they like from this.
 */
export function useKmagic() {
  const projectId = useEditor((s) => s.projectId);
  const previewMagicResult = useEditor((s) => s.previewMagicResult);
  const commitMagicPreview = useEditor((s) => s.commitMagicPreview);
  const rollbackMagicPreview = useEditor((s) => s.rollbackMagicPreview);
  const resetToInitial = useEditor((s) => s.resetToInitial);
  const inPreview = useEditor((s) => !!s.magicPreview);

  const [instruction, setInstruction] = useState('');
  const [phase, setPhase] = useState<KmagicPhase>('idle');
  const [magic, setMagic] = useState<MagicState | null>(null);
  const [quotaError, setQuotaError] = useState<string | null>(null);
  const [msgIdx, setMsgIdx] = useState(0);
  const pollRef = useRef<number | null>(null);

  // Rotate the cute status line every ~2.5s while running.
  useEffect(() => {
    if (phase !== 'running') return;
    setMsgIdx((i) => (i + 1) % MAGIC_MESSAGES.length);
    const id = window.setInterval(() => setMsgIdx((i) => (i + 1) % MAGIC_MESSAGES.length), 2500);
    return () => window.clearInterval(id);
  }, [phase]);
  const runningMessage = MAGIC_MESSAGES[msgIdx]!;

  const stopPoll = () => {
    if (pollRef.current) window.clearInterval(pollRef.current);
    pollRef.current = null;
  };
  const poll = () => {
    if (!projectId) return;
    api
      .getMagic(projectId)
      .then((m) => {
        setMagic(m);
        if (m.status === 'running') setPhase('running');
        else if (m.status === 'done') {
          setPhase((p) => (p === 'previewing' ? p : m.applied ? 'idle' : 'ready'));
          stopPoll();
        } else if (m.status === 'failed') {
          setPhase('idle');
          stopPoll();
        }
      })
      .catch(() => undefined);
  };

  // Resume any in-flight/completed run when mounted.
  useEffect(() => {
    if (!projectId) return;
    poll();
    return stopPoll;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId]);

  const start = async () => {
    if (!projectId) return;
    setQuotaError(null);
    setPhase('running');
    setMagic({ status: 'running', stage: 'queued', progress: 0, log: [] });
    try {
      await api.startMagic(projectId, instruction.trim() || undefined);
      stopPoll();
      pollRef.current = window.setInterval(poll, 1800);
    } catch (err) {
      setPhase('idle');
      setMagic(null);
      if (err instanceof ApiClientError && err.code === 'quota_exceeded') {
        setQuotaError(err.message);
      }
    }
  };

  const showResult = () => {
    if (magic?.result) {
      previewMagicResult(magic.result);
      setPhase('previewing');
    }
  };

  const applyResult = async () => {
    commitMagicPreview();
    setPhase('idle');
    setMagic((m) => (m ? { ...m, applied: true } : m));
    if (projectNeedsDepth() && useEditor.getState().cutout.status !== 'ready') {
      useEditor.getState().setDepthDialogOpen(true);
    }
    try {
      await saveEdits();
      if (projectId) await api.markMagicApplied(projectId, magic?.id);
    } catch {
      /* autosave will retry */
    }
  };

  const rollback = () => {
    rollbackMagicPreview();
    setPhase('ready');
  };

  const reset = async () => {
    if (!projectId) return;
    if (!window.confirm('Reset this project to its original state? This clears the template, colors, position, hooks, B-roll, music and emphasis you (or kMagic) changed. The transcript text is kept.')) return;
    resetToInitial();
    try {
      await saveEdits();
    } catch {
      /* autosave will retry */
    }
  };

  const runAgain = () => {
    setPhase('idle');
    setMagic(null);
  };

  return {
    projectId,
    instruction,
    setInstruction,
    phase,
    magic,
    inPreview,
    quotaError,
    runningMessage,
    pct: Math.round((magic?.progress ?? 0) * 100),
    result: magic?.result,
    start,
    showResult,
    applyResult,
    rollback,
    reset,
    runAgain,
  };
}
